import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generatePdf } from '../utils/pdfGenerator';

const { FiX, FiMail, FiCheckCircle, FiAlertTriangle } = FiIcons;

const PdfForm = ({ formData, results, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const captchaRef = useRef(null);
  const captchaRenderedRef = useRef(false);
  const SOURCE_NAME = 'Profitability Calculator';

  const initializeCaptcha = () => {
    if (window.grecaptcha && !captchaRenderedRef.current) {
      window.grecaptcha.render(captchaRef.current, {
        sitekey: '6LdSD5orAAAAAGW3mWsKGYmx8KkGYrelUJtDLVUX',
        callback: () => setCaptchaVerified(true),
        'expired-callback': () => setCaptchaVerified(false)
      });
      captchaRenderedRef.current = true;
      setCaptchaLoaded(true);
    }
  };

  useEffect(() => {
    const existingScript = document.querySelector('script[src*="recaptcha"]');
  
    if (!window.grecaptcha && !existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  
    const maxAttempts = 10;
    let attempt = 0;
    const interval = setInterval(() => {
      attempt++;
      if (window.grecaptcha && captchaRef.current && !captchaRenderedRef.current) {
        initializeCaptcha();
        clearInterval(interval);
      }
      if (attempt >= maxAttempts) clearInterval(interval);
    }, 200);
  
    return () => {
      setCaptchaVerified(false);
      setCaptchaLoaded(false);
      captchaRenderedRef.current = false;
  
      if (captchaRef.current) {
        captchaRef.current.innerHTML = '';
      }
  
      const script = document.querySelector('script[src*="recaptcha"]');
      if (script) document.head.removeChild(script);
  
      clearInterval(interval);
    };
  }, []);
  
  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isFormValid = () =>
    firstName.trim() !== '' &&
    isEmailValid(email) &&
    consent &&
    captchaVerified &&
    !isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      console.log('Form validation failed');
      return;
    }

    setIsSubmitting(true);

    try {
      const trimmedFirstName = firstName.trim();
      const pdf = generatePdf(formData, results);
      const arrayBuffer = pdf.output('arraybuffer');
      const base64Encoded = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const payload = { firstName: trimmedFirstName, email, pdfBase64: base64Encoded };

      try {
        const vboutRes = await fetch('https://w6x7b01965.execute-api.us-east-1.amazonaws.com/default/UpdateVboutFromWebApp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName: trimmedFirstName, email, source: SOURCE_NAME })
        });
        const vboutJson = await vboutRes.json();
        console.log('VBOUT response:', vboutJson);
      } catch (vboutError) {
        console.warn('VBOUT update failed:', vboutError);
      }

      const response = await fetch('https://capwfl1c0e.execute-api.us-east-1.amazonaws.com/prod/SendPdfFromJodieViaPPS', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const rawResult = await response.json();
      const result = typeof rawResult.body === 'string' ? JSON.parse(rawResult.body) : rawResult;

      if (response.ok && result.result === 'Success') {
        setSubmitStatus({
          success: true,
          message: "Success! Your PDF has been sent to your email from Jodie Willmer.",
        });
        setTimeout(onClose, 3000);
      } else {
        throw new Error(result.error || 'Unexpected error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({
        success: false,
        message: "Sorry, something went wrong. Please try again.",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 20 }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isSubmitting}
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>

        <div className="text-center mb-6">
          <div className="bg-blue-100 p-3 rounded-full inline-flex items-center justify-center mb-4">
            <SafeIcon icon={FiMail} className="text-blue-600 text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Get Your PDF Report</h3>
          <p className="text-gray-600 text-sm mt-1">
            We'll email you a detailed PDF with your calculation results
          </p>
        </div>

        {submitStatus ? (
          <motion.div
            className={`p-4 rounded-lg ${
              submitStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            } mb-4`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-start space-x-3">
              <SafeIcon
                icon={submitStatus.success ? FiCheckCircle : FiAlertTriangle}
                className={`${submitStatus.success ? 'text-green-600' : 'text-red-600'} text-xl mt-0.5 flex-shrink-0`}
              />
              <p className={`${submitStatus.success ? 'text-green-600' : 'text-red-600'} text-sm leading-relaxed`}>
                {submitStatus.message}
              </p>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Your first name"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  email && !isEmailValid(email) ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="your@email.com"
                required
                disabled={isSubmitting}
              />
              {email && !isEmailValid(email) && (
                <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
              )}
            </div>

            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                id="consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
                disabled={isSubmitting}
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                I consent to receiving emails with my calculation results and related information. <span className="text-red-500">*</span>
              </label>
            </div>

            <div className="flex justify-center my-4">
              <div id="recaptcha-container" ref={captchaRef}></div>
            </div>

            <button
              type="submit"
              className={`w-full py-2 px-4 rounded-lg font-medium text-white transition-colors ${
                isFormValid()
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sending...</span>
                </span>
              ) : (
                'Send me my calculation as a PDF'
              )}
            </button>

            <p className="text-xs text-gray-500 mt-2">
              Your data will only be used to deliver this PDF. This tool provides general information only and does not constitute financial or legal advice.
            </p>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PdfForm;
